import { apiFetch } from '../../../../utils/api';

// Value Delete Handler
export const handleDeleteValue = (
    selectedFeatureId,
    selectedValueId,
    selectedValue,
    setSelectedValueId,
    fetchData,
    confirmDelete,
    setFeatureValues
) => async () => {
    if (!selectedFeatureId || !selectedValueId) return;
    const valText = selectedValue?.value || '';
    if (!await confirmDelete({ kind: 'value', featureId: selectedFeatureId, valueId: selectedValueId, name: valText })) return;

    try {
        const res = await apiFetch(`/api/features/${selectedFeatureId}/values/${selectedValueId}`, {
            method: 'DELETE'
        });
        if (res.ok) {
            const deletedId = selectedValueId;
            setFeatureValues(prev => prev.filter(v => v.id !== deletedId));
            setSelectedValueId(null);
            await fetchData();
        } else {
            alert(`Failed to delete value: ${await res.text()}`);
        }
    } catch (err) {
        console.error(err);
    }
};

// Value CRUD Handlers
export const handleAddValue = async (
    e,
    newValueText,
    selectedFeatureId,
    setNewValueText,
    setIsAddingValue,
    selectedFamilyId,
    setFeatures,
    setSelectedFeatureId,
    handleSelectFeature,
    fetchData
) => {
    e.preventDefault();
    if (!newValueText.trim() || !selectedFeatureId) return;
    setIsAddingValue(true);
    try {
        const res = await apiFetch(`/api/features/${selectedFeatureId}/values`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Value: newValueText.trim() })
        });
        if (res.ok) {
            const data = await res.json();
            setNewValueText('');
            // refresh features for selected family
            if (selectedFamilyId) {
                // Use family-scoped endpoint instead of deprecated /api/features
                const featRes = await apiFetch(`/api/products/${selectedFamilyId}/features`);
                if (featRes.ok) {
                    const featData = await featRes.json();
                    const updatedFeat = featData.find(v => v.id === selectedFeatureId);
                    if (updatedFeat) {
                        const newValId = data.id || data.Id || data.ID;
                        // Need to call handleSelectFeature here
                        // For now, just refresh
                        await fetchData({ selectFeatureId: selectedFeatureId, selectValueId: newValId });
                    }
                }
            }
        } else {
            alert(`Failed to add value: ${await res.text()}`);
        }
    } catch (err) {
        console.error(err);
    } finally {
        setIsAddingValue(false);
    }
};

export const handleAddFeatureInline = async (
    e,
    newFeatureInlineName,
    selectedFamilyId,
    setNewFeatureInlineName,
    setIsAddingFeatureInline,
    fetchData
) => {
    e.preventDefault();
    if (!newFeatureInlineName.trim() || !selectedFamilyId) return;
    setIsAddingFeatureInline(true);
    try {
        const res = await apiFetch(`/api/features`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                Name: newFeatureInlineName.trim(),
                IsGlobal: false,
                FamilyId: selectedFamilyId
            })
        });

        if (res.ok) {
            const data = await res.json();
            const newFeatId = data.id || data.Id || data.ID;
            setNewFeatureInlineName('');
            await fetchData({ selectFeatureId: newFeatId });
        } else {
            alert(`Error creating feature: ${await res.text()}`);
        }
    } catch (err) {
        console.error(err);
    } finally {
        setIsAddingFeatureInline(false);
    }
};

// Attribute and value handlers
export const handleAddAttrRow = (setCustomAttrs) => () => {
    setCustomAttrs(prev => [...prev, { key: '', value: '' }]);
};

export const handleRemoveAttrRow = (setCustomAttrs) => (idx) => {
    setCustomAttrs(prev => prev.filter((_, i) => i !== idx));
};

export const handleAttrChange = (setCustomAttrs) => (idx, field, value) => {
    setCustomAttrs(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
};

export const handleValueFieldChange = (setFeatureValues) => (valIdx, field, value) => {
    setFeatureValues(prev => prev.map((v, i) => i === valIdx ? { ...v, [field]: value } : v));
};

export const handleSetDefaultValue = (setFeatureValues) => (valueId) => {
    setFeatureValues(prev => prev.map(v => ({
        ...v,
        isDefault: Boolean(valueId) && String(v.id) === String(valueId)
    })));
};

export const handleAddValueAttr = (setFeatureValues) => (valIdx) => {
    setFeatureValues(prev => prev.map((v, i) => i === valIdx ? {
        ...v,
        attributes: [...(v.attributes || []), { key: '', value: '' }]
    } : v));
};

export const handleRemoveValueAttr = (setFeatureValues) => (valIdx, attrIdx) => {
    setFeatureValues(prev => prev.map((v, i) => i === valIdx ? {
        ...v,
        attributes: v.attributes?.filter((_, ai) => ai !== attrIdx) || []
    } : v));
};

export const handleValueAttrChange = (setFeatureValues) => (valIdx, attrIdx, field, value) => {
    setFeatureValues(prev => prev.map((v, i) => i === valIdx ? {
        ...v,
        attributes: v.attributes?.map((a, ai) => ai === attrIdx ? { ...a, [field]: value } : a) || []
    } : v));
};

export const handleValuePropertyChange = (setFeatureValues) => (valIdx, key, value) => {
    setFeatureValues(prev => prev.map((item, i) => i === valIdx ? {
        ...item,
        propertyValues: {
            ...(item.propertyValues || {}),
            [key]: value
        }
    } : item));
};

export const handleReorderValues = (selectedFeatureId, featureValues, setFeatureValues, families, setFamilies) => async (featureId, draggedId, targetId) => {
    const apply = (values) => {
        const next = [...(values || [])];
        const from = next.findIndex(item => String(item.id) === String(draggedId));
        const to = next.findIndex(item => String(item.id) === String(targetId));
        if (from < 0 || to < 0) return values;
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        return next.map((entry, index) => ({ ...entry, sortOrder: index }));
    };

    let nextValues = null;
    if (String(featureId) === String(selectedFeatureId)) {
        nextValues = apply(featureValues);
        setFeatureValues(nextValues);
    } else {
        const feature = (families || []).flatMap(family => family.features || []).find(item => String(item.id) === String(featureId));
        nextValues = apply(feature?.values || []);
    }

    const orderedIds = (nextValues || []).map(item => item.id).filter(Boolean);
    if (!orderedIds.length) return;

    setFamilies(prev => prev.map(family => ({
        ...family,
        features: (family.features || []).map(feature =>
            String(feature.id) === String(featureId) ? { ...feature, values: nextValues } : feature
        )
    })));

    try {
        await apiFetch(`/api/features/${featureId}/values/reorder`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ valueIds: orderedIds })
        });
    } catch (err) {
        console.error(err);
    }
};

// Save Feature Properties
export const handleSaveFeatureProperties = (
    selectedFeatureId,
    friendlyName,
    description,
    inputType,
    customAttrs,
    featureValues,
    selectedFamilyId,
    selectedValueId,
    setSavingFeature,
    setFeatures,
    handleSelectFeature,
    fetchData,
    valueSchema = [],
    isSystem = false,
    cardLayout = null,
    isOutput = false
) => async (e) => {
    e.preventDefault();
    if (!selectedFeatureId) return;

    setSavingFeature(true);
    try {
        const attrsObj = {};
        customAttrs.forEach(item => {
            if (item.key.trim()) {
                attrsObj[item.key.trim()] = item.value.trim();
            }
        });

        const valuesPayload = featureValues.map(val => {
            const valAttrs = {};
            (val.attributes || []).forEach(attr => {
                if (attr.key.trim()) {
                    valAttrs[attr.key.trim()] = attr.value.trim();
                }
            });
            return {
                id: val.id,
                value: val.value,
                displayText: val.displayText,
                description: val.description,
                imageUrl: val.imageUrl,
                isDefault: Boolean(val.isDefault),
                sortOrder: Number(val.sortOrder || 0),
                propertyValues: val.propertyValues || {},
                attributes: valAttrs
            };
        });

        const res = await apiFetch(`/api/features/${selectedFeatureId}/properties`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                displayText: friendlyName,
                description: description,
                inputType: inputType,
                attributes: attrsObj,
                valueSchema: valueSchema || [],
                cardLayout,
                isSystem: Boolean(isSystem),
                isOutput: Boolean(isOutput),
                values: valuesPayload
            })
        });

        if (res.ok) {
            // Reload feature details for selected family
            if (selectedFamilyId) {
                // Use family-scoped endpoint instead of deprecated /api/features
                const featRes = await apiFetch(`/api/products/${selectedFamilyId}/features`);
                if (featRes.ok) {
                    const featData = await featRes.json();
                    setFeatures(featData);

                    // Re-select active feature to update pane variables and preserve selected value
                    const updatedFeat = featData.find(v => v.id === selectedFeatureId);
                    if (updatedFeat) {
                        handleSelectFeature(updatedFeat, selectedValueId);
                        await fetchData();
                    }
                }
            }
            alert('Feature configurations saved successfully.');
        } else {
            alert(`Error saving properties: ${await res.text()}`);
        }
    } catch (err) {
        console.error('Failed to save feature properties', err);
    } finally {
        setSavingFeature(false);
    }
};

// Additional handlers for inline feature/value operations
export const handleAddValueInline = (
    inlineNewValTexts,
    selectedFamilyId,
    setInlineNewValTexts,
    setFeatures,
    handleSelectFeature,
    fetchData
) => async (e, featureId) => {
    e.preventDefault();
    const valText = (inlineNewValTexts[featureId] || '').trim();
    if (!valText || !featureId) return;

    try {
        const res = await apiFetch(`/api/features/${featureId}/values`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Value: valText })
        });
        if (res.ok) {
            const data = await res.json();
            setInlineNewValTexts(prev => ({ ...prev, [featureId]: '' }));
            
            // Refresh features for selected family
            if (selectedFamilyId) {
                // Use family-scoped endpoint instead of deprecated /api/features
                const featRes = await apiFetch(`/api/products/${selectedFamilyId}/features`);
                if (featRes.ok) {
                    const featData = await featRes.json();
                    setFeatures(featData);
                    const updatedFeat = featData.find(v => v.id === featureId);
                    if (updatedFeat) {
                        const newValId = data.id || data.Id || data.ID;
                        handleSelectFeature(updatedFeat, newValId);
                        await fetchData();
                    }
                }
            }
        } else {
            alert(`Failed to add value: ${await res.text()}`);
        }
    } catch (err) {
        console.error(err);
    }
};

export const handleRenameFeatureSubmit = (
    selectedFeatureId,
    editFeatureNameText,
    selectedFamilyId,
    setIsSavingFeatureName,
    setFeatures,
    handleSelectFeature,
    setIsEditingFeatureName,
    selectedValueId
) => async (e) => {
    if (e) e.preventDefault();
    if (!selectedFeatureId || !editFeatureNameText.trim()) return;
    setIsSavingFeatureName(true);
    try {
        const res = await apiFetch(`/api/features/${selectedFeatureId}/rename`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Name: editFeatureNameText.trim() })
        });

        if (res.ok) {
            // Refresh features for selected family
            if (selectedFamilyId) {
                // Use family-scoped endpoint instead of deprecated /api/features
                const featRes = await apiFetch(`/api/products/${selectedFamilyId}/features`);
                if (featRes.ok) {
                    const featData = await featRes.json();
                    setFeatures(featData);
                    const updatedFeat = featData.find(v => v.id === selectedFeatureId);
                    if (updatedFeat) {
                        handleSelectFeature(updatedFeat, selectedValueId);
                        await fetchData();
                    }
                }
            }
            setIsEditingFeatureName(false);
        } else {
            alert(`Error renaming feature: ${await res.text()}`);
        }
    } catch (err) {
        console.error(err);
    } finally {
        setIsSavingFeatureName(false);
    }
};

export const handleRenameValueSubmit = (
    selectedFeatureId,
    selectedValueId,
    editValueNameText,
    selectedFamilyId,
    setIsSavingValueName,
    setFeatures,
    handleSelectFeature,
    setIsEditingValueName
) => async (e) => {
    if (e) e.preventDefault();
    if (!selectedFeatureId || !selectedValueId || !editValueNameText.trim()) return;
    setIsSavingValueName(true);
    try {
        const res = await apiFetch(`/api/features/${selectedFeatureId}/values/${selectedValueId}/rename`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Value: editValueNameText.trim() })
        });

        if (res.ok) {
            // Refresh features for selected family
            if (selectedFamilyId) {
                // Use family-scoped endpoint instead of deprecated /api/features
                const featRes = await apiFetch(`/api/products/${selectedFamilyId}/features`);
                if (featRes.ok) {
                    const featData = await featRes.json();
                    setFeatures(featData);
                    const updatedFeat = featData.find(v => v.id === selectedFeatureId);
                    if (updatedFeat) {
                        handleSelectFeature(updatedFeat, selectedValueId);
                        await fetchData();
                    }
                }
            }
            setIsEditingValueName(false);
        } else {
            alert(`Error renaming value: ${await res.text()}`);
        }
    } catch (err) {
        console.error(err);
    } finally {
        setIsSavingValueName(false);
    }
};
